import * as mutationTypes from '../mutation-types'
import * as actionTypes from '../action-types'
import {
  RESET_ACCESSIBLE_ASSESSMENTS_DATA,
  RESET_ACCESSIBLE_ATTENDANCE_DATA,
  RESET_ACCESSIBLE_ENGAGEMENT_DATA,
  RESET_ACCESSIBLE_INTERACTIONS_DATA,
  RESET_ACCESSIBLE_MODULES_DATA, RESET_ACCESSIBLE_RESOURCES_DATA
} from './accessibility/accessible-action-types'
import server from '../server'
import store from '../../store'
import jwtDecode from 'jwt-decode'
import axios from 'axios'
import router from '../../router'
import { informationTexts } from './information'
import {ThemeAppearanceUtils} from '../../utils/theme-appearance-utils'

const state = {
  loading: false,
  loggedIn: false,
  loggedOut: false,
  loginToken: '',
  loginError: false,
  sessionExpired: false,
  loggedOutSnackbar: false,
  sessionExpires: null,
  roles: [],
  featureFlags: [],
  username: '',
  studentId: '',
  title: '',
  firstName: '',
  lastName: '',
  darkTheme: ThemeAppearanceUtils.getDefaultDarkThemePreference(),
  isMenuOpen: false,
  sessionExpiredActions: [],
  isTutor: false,
  chartLimitReached: false,
  previousRoute: {},
  module: {
    alert: 'ALERT',
    attendance: 'ATTENDANCE',
    assessment: 'ASSESSMENT',
    engagement: 'ENGAGEMENT',
    interexem: 'INTEREXEM',
    studentmods: 'STUMOD',
    insights: 'INSIGHTS',
    feeds: 'FEEDS',
    student: 'STUDENT',
    help: 'HELP',
    config: 'CONFIG',
    sdpurge: 'SDPURGE'
  },
  functions: {
    adminMenu: 'ADMINMNU',
    headerMenu: 'HEADERMNU',
    helpPopUp: 'HELPPOPUP',
    sdpurgeMenu: 'SDPURGEMNU'
  },
  options: {
    enable: {
      alerts: 'ENBALERTS',
      interactions: 'ENBINTEACT',
      helpPopUp: 'ENBHELPPOP',
      insightsForStaff: 'ENBINSSTAF',
      config: 'ENMAINMENU',
      sdpMenu: 'ENBSDPMNU'
    }
  },
  accessibility: false,
  accessible: false
}

const getters = {
  IS_ADMIN (state) {
    return state.roles.indexOf('ROLE_ADMIN') >= 0
  },
  IS_STUDENT (state) {
    return state.roles.indexOf('ROLE_STUDENT') >= 0
  },
  IS_STAFF (state) {
    return state.roles.indexOf('ROLE_STAFF') >= 0
  },
  USER_NAME (state) {
    return (state.title ? state.title + ' ' : '') +
        (state.firstName ? state.firstName + ' ' : '') +
        (state.lastName ? state.lastName : '')
  },
  HAS_FEATURE_FLAG: (state) => (flag) => {
    return state.featureFlags.indexOf(flag) >= 0
  },
  IS_TUTOR (state) {
    return state.isTutor
  },
  IS_ACCESSIBLE (state) {
    return state.accessible
  }
}

function updateLoginToken (state, loginToken) {
  state.loggedIn = true
  state.sessionExpired = false
  state.loginToken = loginToken
  state.loginError = false
  const decoded = jwtDecode(loginToken.substring(7))
  state.roles = decoded.roles.split(',')
  state.username = decoded.sub.toLowerCase().trim()
  state.featureFlags = decoded['product-feature-flags']
  if (state.featureFlags === undefined) {
    state.featureFlags = []
  }
  if (decoded.details) {
    state.title = decoded.details.title
    state.firstName = decoded.details.firstName
    state.lastName = decoded.details.lastName
    state.studentId = decoded.details.studentId
  }
  state.sessionExpires = decoded.exp * 1000
}

function updateAccessibility (state, loginToken) {
  const decoded = jwtDecode(loginToken.substring(7))
  if (decoded.details) {
    state.accessible = decoded.details.accessibility
  }
}

const mutations = {
  [mutationTypes.SET_STUDENT_ACCESSIBILITY] (state, val) {
    state.accessible = val
  },
  [mutationTypes.LOGGED_IN] (state, loginToken) {
    state.loggedOut = false
    updateLoginToken(state, loginToken)
    updateAccessibility(state, loginToken)
  },
  [mutationTypes.REFRESH_AUTHORIZATION] (state, loginToken) {
    if (!state.loggedOut) {
      updateLoginToken(state, loginToken)
    }
  },
  [mutationTypes.LOGGED_OUT] (state) {
    state.loggedOut = true
    state.accessible = false
    state.accessibility = false
    state.loggedIn = false
    state.loginToken = ''
    server.removeAuthOnPage()
    state.loggedOutSnackbar = true
    state.username = ''
    state.studentId = ''
    state.title = ''
    state.firstName = ''
    state.lastName = ''
    state.sessionExpired = false
    state.sessionExpiredActions = []
    state.isTutor = false
    if (store.state.config.useSAML) {
      window.location.replace(store.state.config.authUrlLogOut)
    }
  },
  [mutationTypes.SET_PREVIOUS_ROUTE] (state, name) {
    state.previousRoute = name
  },
  [mutationTypes.LOG_IN_FAILURE] (state) {
    state.loginError = true
  },
  [mutationTypes.LOG_IN_ERROR_RESET] (state) {
    state.loginError = false
  },
  [mutationTypes.LOGOUT_SNACKBAR_OFF] (state) {
    state.loggedOutSnackbar = false
  },
  [mutationTypes.SET_PROFILE_DARK_THEME] (state, value) {
    ThemeAppearanceUtils.setDarkThemePreference(value)
    state.darkTheme = value
  },
  [mutationTypes.SET_PERSONAL_MENU] (state, value) {
    state.isMenuOpen = value
  },
  [mutationTypes.SAVE_SESSION_EXPIRED_ACTION] (state, actionData) {
    state.sessionExpired = true
    state.sessionExpiredActions.push(actionData)
  },
  [mutationTypes.CLEAR_SESSION_EXPIRED_ACTIONS] (state) {
    state.sessionExpiredActions = []
  },
  [mutationTypes.SET_IS_TUTOR] (state, isNotTutor) {
    state.isTutor = !isNotTutor
  },
  [mutationTypes.SET_CHART_LIMIT_REACHED] (state, limitReached) {
    state.chartLimitReached = limitReached
  },
  [mutationTypes.SET_CURRENT_ACCESSIBILITY_FLAG] (state, flag) {
    state.accessible = flag
  },
  [mutationTypes.SET_LOGIN_LOADING] (state, value) {
    state.loading = value
  }
}

const actions = {
  [actionTypes.LOGIN] (context, credentials) {
    context.commit(mutationTypes.SET_LOGIN_LOADING, true)
    context.commit(mutationTypes.LOG_IN_ERROR_RESET)
    let {username, password} = credentials
    server.unauthPost('/core/login', {username, password}, response => {
      context.commit(mutationTypes.LOGGED_IN, response.headers.authorization)
      context.commit(mutationTypes.REMOVE_GROUP_STORAGE, {})
      server.setAuthOnPage(response.headers.authorization)
      context.dispatch(actionTypes.APPLY_SESSION_EXPIRED_ACTIONS)
      // context.dispatch(actionTypes.GET_TUTOR_BOOLEAN)
      // context.dispatch(actionTypes.GET_CHART_LIMIT_BOOLEAN)
      const locale = context.rootState.i18n.locale
      context.dispatch(actionTypes.GET_HELP_TOPICS_FOR_LOCALE, {locale})
      if (store.getters.IS_STUDENT) {
        context.dispatch(actionTypes.GET_CURRENT_ACCESSIBILITY_FLAG)
        context.commit(mutationTypes.RESET_STUDENT)
        context.dispatch(RESET_ACCESSIBLE_ASSESSMENTS_DATA)
        context.dispatch(RESET_ACCESSIBLE_ATTENDANCE_DATA)
        context.dispatch(RESET_ACCESSIBLE_ENGAGEMENT_DATA)
        context.dispatch(RESET_ACCESSIBLE_INTERACTIONS_DATA)
        context.dispatch(RESET_ACCESSIBLE_MODULES_DATA)
        context.dispatch(RESET_ACCESSIBLE_RESOURCES_DATA)
      }
      context.commit(mutationTypes.SET_INFORMATION_SNACKBAR_TEXT, informationTexts.LOGIN_SUCCESS)
      context.commit(mutationTypes.SET_INFORMATION_SNACKBAR_STATE, true)
      context.commit(mutationTypes.SET_CATEGORY_LOADING, false)
      context.commit(mutationTypes.SET_LOGIN_LOADING, false)
    }, () => {
      context.commit(mutationTypes.LOG_IN_FAILURE)
      context.commit(mutationTypes.SET_LOGIN_LOADING, false)
    })
  },
  [actionTypes.LOGOUT] (context) {
    axios({
      url: `${context.rootState.config.baseUrl}:${context.rootState.config.port}/core/token/destroy`,
      method: 'post',
      headers: {
        'Authorization': context.rootState.profile.loginToken
      }
    })
    context.commit(mutationTypes.LOGGED_OUT)
  },
  [actionTypes.APPLY_SESSION_EXPIRED_ACTIONS] ({state, commit, dispatch}) {
    // Prevent grouped calls being repeated
    let processed = new Set()
    state.sessionExpiredActions.forEach(({action, data}) => {
      if (!processed.has(action)) {
        dispatch(action, data)
        processed.add(action)
      }
    })
    commit(mutationTypes.CLEAR_SESSION_EXPIRED_ACTIONS)
  },
  [actionTypes.REFRESH_LOGIN] ({state, commit}, {config, headers, request}) {
    if (headers.authorization !== undefined) {
      const decoded = jwtDecode(headers.authorization.substring(7))
      let roles = decoded.roles

      if ((!roles.includes('ROLE_SYSTEM')) && !config.url.endsWith('/login')) {
        if (headers.authorization !== state.loginToken) {
          commit(mutationTypes.REFRESH_AUTHORIZATION, headers.authorization)
          server.setAuthOnPage(headers.authorization)
        }
      }
    }
  },
  [actionTypes.GET_TUTOR_BOOLEAN] ({commit}) {
    const actionData = { action: actionTypes.GET_TUTOR_BOOLEAN, data: undefined }
    server.getReplayable(`/core/staff/isTutor`, actionData).then(response => {
      // set boolean for 'SET_IS_TUTOR' (which is assigned to the state of dashboard, my studnets and interactions)
      commit(mutationTypes.SET_IS_TUTOR, response.data)
    })
  },
  [actionTypes.GET_CHART_LIMIT_BOOLEAN] ({commit}) {
    const actionData = { action: actionTypes.GET_CHART_LIMIT_BOOLEAN, data: undefined }
    server.getReplayable(`/core/chart/isLimitReached`, actionData).then(response => {
      commit(mutationTypes.SET_CHART_LIMIT_REACHED, response.data)
    }).catch((err) => {
      console.log('Failed to check chart limit')
      console.error(err)
    })
  },
  [actionTypes.LOGIN_SSO_ACTION] ({commit, dispatch}, token) {
    commit(mutationTypes.LOG_IN_ERROR_RESET)
    let username = token
    let password = ''
    // server.unauthPost(`/core/auth/ssologin/${token}`, {}, response => {
    server.unauthPost('/core/ssologin', {username, password}, response => {
      commit(mutationTypes.LOGGED_IN, response.headers.authorization)
      server.setAuthOnPage(response.headers.authorization)
      dispatch(actionTypes.APPLY_SESSION_EXPIRED_ACTIONS)
      commit(mutationTypes.REMOVE_GROUP_STORAGE, {})
      dispatch(actionTypes.GET_TUTOR_BOOLEAN)
      dispatch(actionTypes.GET_CHART_LIMIT_BOOLEAN)
      if (store.getters.IS_STUDENT) {
        dispatch(actionTypes.GET_CURRENT_ACCESSIBILITY_FLAG)
      }
    }, () => {
      commit(mutationTypes.LOG_IN_FAILURE)
    })
  },
  [actionTypes.UPDATE_STUDENT_ACCESSIBILITY] ({rootState, state, commit}, {studentId, active}) {
    const payload = { studentId, active }
    axios({
      url: `${rootState.config.baseUrl}:${rootState.config.port}/core/student/${studentId}/accessibility/${active}`,
      method: 'put',
      headers: {
        'Authorization': rootState.profile.loginToken
      },
      actionData: {action: actionTypes.UPDATE_STUDENT_ACCESSIBILITY, data: payload}
    }).then((res) => {
      commit(mutationTypes.SET_CURRENT_ACCESSIBILITY_FLAG, active)
      router.push({name: active ? 'StudentView' : 'StudentHome'})
    }).catch((err) => {
      console.log('Failed to activate/deactivate accessibility')
      console.error(err)
    })
  },
  [actionTypes.GET_CURRENT_ACCESSIBILITY_FLAG] ({state, commit}) {
    const actionData = { action: actionTypes.GET_CURRENT_ACCESSIBILITY_FLAG, data: undefined }
    server.getReplayable(`/core/student/${state.studentId}/accessibleView`, actionData).then(response => {
      commit(mutationTypes.SET_CURRENT_ACCESSIBILITY_FLAG, response.data)
    })
  },
  [actionTypes.UPDATE_CURRENT_ACCESSIBILITY_FLAG] ({rootState, state, commit}, {flag}) {
    let studentIdentifier = state.studentId
    const payload = {studentIdentifier, flag}
    axios({
      url: `${rootState.config.baseUrl}:${rootState.config.port}/core/student/${studentIdentifier}/accessibility/${flag}`,
      method: 'put',
      headers: {
        'Authorization': rootState.profile.loginToken
      },
      actionData: {action: actionTypes.UPDATE_CURRENT_ACCESSIBILITY_FLAG, data: payload}
    }).then((res) => {
      commit(mutationTypes.SET_CURRENT_ACCESSIBILITY_FLAG, flag)
    }).catch((err) => {
      console.log(`Failed to activate/deactivate accessibility.`)
      console.error(err)
    })
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/profileModule.js